<script setup lang="ts">
import { RouterLink, type RouteLocationRaw } from 'vue-router'

// One row of a `UiList` (an iOS settings row): a flex line with the padding and minimum height of a touch target, and a divider
// to the row above. With `to` it is a `RouterLink`, `as` picks another element (`label`, `button`, ...). A `label` (text, or the
// `label` slot for markup) puts a fixed width caption in front; the element right after it is the value and takes the rest of
// the line. `stackNarrow` puts the value below the label on a narrow screen (for values that need the width, like a segmented
// control), `stacked` keeps the label at the top of a tall value.
withDefaults(
  defineProps<{
    as?: string
    to?: RouteLocationRaw
    label?: string
    stackNarrow?: boolean
    stacked?: boolean
  }>(),
  {
    as: 'div',
  },
)
</script>

<template>
  <component
    :is="to ? RouterLink : as"
    :to="to"
    class="ui-row"
    :class="{ 'stack-narrow': stackNarrow, stacked }"
  >
    <span v-if="label !== undefined || $slots.label" class="label"
      ><slot name="label">{{ label }}</slot></span
    >
    <slot />
  </component>
</template>

<style scoped>
.ui-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.9rem;
  padding: 0.45rem 0.85rem;
}

.ui-row + .ui-row {
  border-top: 1px solid var(--tg-secondary-bg-color);
}

.label {
  flex: 0 0 7.5rem;
  color: var(--tg-hint-color);
  font-size: 0.92rem;
}

.label + :slotted(*) {
  flex: 1;
  min-width: 0;
}

.stacked {
  align-items: flex-start;
}

.stacked > .label {
  padding-top: 0.5rem;
}

@media (max-width: 420px) {
  .label {
    flex-basis: 5.8rem;
  }
}

/* A value that needs more room than the label leaves: label on top, value below at the full width. */
@media (max-width: 26rem) {
  .stack-narrow {
    flex-direction: column;
    align-items: stretch;
    gap: 0.35rem;
  }

  .stack-narrow > .label,
  .stack-narrow > .label + :slotted(*) {
    flex: none;
  }
}
</style>
